import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { graphql } from 'react-relay'
import { object, string } from 'yup'
import { useFormikContext } from 'formik'
import { Location, useNavigate, useLocation } from 'react-router-dom'

import { View, YStack, isWeb } from 'tamagui'
import { Form } from '@newComponents/Form/Form'

import { useAuthContext } from '@data/useAuthContext'
import { useAuthRedirect } from '@data/useAuthRedirect'
import { useMutation } from '@data/useMutation'

import { SignInScreenTokenFormMutation } from './__generated__/SignInScreenTokenFormMutation.graphql'
import { SignInScreenTokenFormPasswordMutation } from './__generated__/SignInScreenTokenFormPasswordMutation.graphql'

const VALIDATION_SCHEMA = object().shape({
  email: string().required('Required'),
})

const GENERATE_SIGN_IN_TOKEN_MUTATION = graphql`
  mutation SignInScreenTokenFormMutation(
    $input: GenerateSignInTokenMutationInput!
  ) {
    generateSignInToken(input: $input) {
      __typename
    }
  }
`

const SIGN_IN_WITH_EMAIL_PASSWORD_MUTATION = graphql`
  mutation SignInScreenTokenFormPasswordMutation(
    $input: SignInWithEmailPasswordMutationInput!
  ) {
    signInWithEmailPassword(input: $input) {
      __typename

      ... on SignInWithEmailPasswordMutationSuccess {
        accessToken
        refreshToken
        query {
          viewer {
            id
            ...UserPrimaryPhotoNew_user
          }
        }
      }
    }
  }
`

export const SignInScreenTokenForm = ({
  initialEmail = '',
  setEmail,
  authRedirectPath,
}: {
  initialEmail?: string
  setEmail?: (email: string) => any
  authRedirectPath?: string
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const authRedirectPromise = useAuthRedirect()
  const { setAuthTokens } = useAuthContext()

  const passwordInputRef = useRef<any>(null)
  const emailInputRef = useRef<any>(null)

  const routerState = useMemo(
    () => ({
      state: {
        backgroundLocation: location.state?.backgroundLocation as Location,
        authRedirectPath: location.state?.authRedirectPath as string,
      },
    }),
    [location]
  )

  const onEmailChange = useCallback(
    (value: string) => {
      if (setEmail) {
        setEmail(value)
      }
    },
    [setEmail]
  )

  const generateSignInTokenMutation =
    useMutation<SignInScreenTokenFormMutation>(GENERATE_SIGN_IN_TOKEN_MUTATION)

  const signInWithEmailPasswordMutation =
    useMutation<SignInScreenTokenFormPasswordMutation>(
      SIGN_IN_WITH_EMAIL_PASSWORD_MUTATION
    )

  const handleSubmit = useCallback(
    async (
      values: { email: string; password: string },
      actions: { setStatus: (status: string) => void }
    ) => {
      // Check both Formik state and DOM element for password value.
      // Password managers may fill the DOM without triggering React events.
      const password =
        values.password ||
        (isWeb
          ? (passwordInputRef.current as HTMLInputElement | null)?.value
          : '') ||
        ''

      if (password) {
        const result = await signInWithEmailPasswordMutation({
          variables: {
            input: { email: values.email, password },
          },
          updater(store) {
            const payload = store.getRootField('signInWithEmailPassword')

            if (
              payload &&
              payload.getValue('__typename') ===
                'SignInWithEmailPasswordMutationSuccess'
            ) {
              const query = payload.getLinkedRecord('query')

              if (query) {
                const root = store.getRoot()
                root.copyFieldsFrom(query)
              }
            }
          },
        })

        if (
          result.signInWithEmailPassword.__typename ===
          'SignInWithEmailPasswordMutationSuccess'
        ) {
          await setAuthTokens(
            result.signInWithEmailPassword.accessToken,
            result.signInWithEmailPassword.refreshToken
          )

          await authRedirectPromise
          return
        }

        // Password auth failed - fall through to email token flow
        // so the user isn't stuck with a cryptic error
      }

      const result = await generateSignInTokenMutation({
        variables: {
          input: { email: values.email, authRedirectPath },
        },
      })

      switch (result.generateSignInToken.__typename) {
        case 'GenerateSignInTokenMutationUnknownEmailError': {
          navigate(`/sign-up?email=${encodeURIComponent(values.email)}`, {
            state: {
              ...routerState.state,
              signInError: 'unknown-email',
            },
          })
          break
        }
        case 'GenerateSignInTokenMutationSuccess': {
          navigate('/auth/code', routerState)
          break
        }
        default: {
          actions.setStatus(
            'There was an error signing you in. Please try again or contact us at hi@guild.host for assistance'
          )
        }
      }
    },
    [
      generateSignInTokenMutation,
      signInWithEmailPasswordMutation,
      setAuthTokens,
      authRedirectPromise,
    ]
  )

  return (
    <Form
      initialValues={{ email: initialEmail, password: '' }}
      onSubmit={handleSubmit}
      validationSchema={VALIDATION_SCHEMA}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <YStack gap='$6'>
        <Form.Field name='email' label='E-mail'>
          <Form.TextInput
            ref={emailInputRef}
            autoFocus
            placeholder='Enter your e-mail address'
            inputMode='email'
            autoComplete='username'
            submitBehaviour='submit-form'
            onChange={onEmailChange}
          />
        </Form.Field>

        <PasswordAutofillDetector
          inputRef={passwordInputRef}
          emailInputRef={emailInputRef}
        />

        <Form.Status />

        <Form.SubmitButton>
          <Form.SubmitButton.Text paddingHorizontal='$4'>
            Sign In
          </Form.SubmitButton.Text>
        </Form.SubmitButton>
      </YStack>
    </Form>
  )
}

/**
 * Hidden password field that detects password manager autofill.
 * Uses the "visually hidden" (sr-only) technique so password managers can
 * detect and fill the input while it remains invisible to sighted users.
 * When autofill is detected (and the email field is also filled),
 * automatically submits the form — the submit handler already handles
 * password-based sign-in by reading the password value from the DOM.
 */
const PasswordAutofillDetector = ({
  inputRef,
  emailInputRef,
}: {
  inputRef: React.RefObject<any>
  emailInputRef: React.RefObject<any>
}) => {
  const { submitForm } = useFormikContext()
  const detectedRef = useRef(false)
  const submitFormRef = useRef(submitForm)
  submitFormRef.current = submitForm

  useEffect(() => {
    if (!isWeb) return

    const detect = () => {
      const el = inputRef.current as HTMLInputElement | null
      if (!el || detectedRef.current) return

      if (el.value) {
        // Only auto-submit if email is also filled (password managers fill both)
        const emailEl = emailInputRef.current as HTMLInputElement | null
        if (!emailEl?.value) return

        detectedRef.current = true
        // Auto-submit — handleSubmit reads the password from the DOM
        setTimeout(() => submitFormRef.current(), 50)
      }
    }

    const el = inputRef.current as HTMLInputElement | null
    el?.addEventListener('input', detect)
    el?.addEventListener('change', detect)

    // Poll as fallback — password managers fill fields asynchronously
    const interval = setInterval(detect, 500)
    // Stop polling after 10 seconds
    const timeout = setTimeout(() => clearInterval(interval), 10_000)

    return () => {
      el?.removeEventListener('input', detect)
      el?.removeEventListener('change', detect)
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [inputRef, emailInputRef])

  return (
    <View
      position='absolute'
      width={1}
      height={1}
      overflow='hidden'
      pointerEvents='none'
      style={{ clip: 'rect(0, 0, 0, 0)' }}
    >
      <Form.Field name='password' label='Password'>
        <Form.TextInput
          ref={inputRef}
          secureTextEntry
          autoComplete='password'
          tabIndex={-1}
        />
      </Form.Field>
    </View>
  )
}
